import gsap from "gsap";
import * as THREE from 'three';
import { ScenesManager } from '../ScenesManager';
import TravelingParticlesShader from '../shaders/TravelingParticles';

import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { CameraManager } from "../CameraManager";
import { MathUtils, Mesh } from "three";
import { parseUrlQuery } from 'utils/url';
import { getFeaturesData } from "utils/localStorage";

export default class TravelingParticles extends THREE.Group {
	uniforms: {  
		uOpacity: { value: number; }; 
		texture1: { value: THREE.Texture; }; 
		uColor: { value: THREE.Color};
		uPointSize: {value: number};
		uLineLength: {value: number},
		uLineThickness: {value: number},
	};
	mesh!: THREE.Points;
	geometry!: THREE.BufferGeometry;
	positions!: Float32Array;
	opacity!: Float32Array;
	lines!: {
		number: number;
		points: THREE.Vector3[];
		speed: number;
		curve: THREE.CatmullRomCurve3;
		geometry: THREE.BufferGeometry;
		max: number;
	}[];
	material!: THREE.ShaderMaterial;
	opacityTween!: gsap.core.Tween;
	materialTween!: gsap.core.Tween;
	messTween!: gsap.core.Tween;
	messFactor: number = 0.0;
	messAmount: number = 0.02;
	lineMaterial!: LineMaterial;
	innerContainer!: THREE.Object3D;
	opacityMultipler: number = 0.3;//use this to control bloom.
	currentTime = 0.0;
	delta = 0.0;
	minDelta = 50.0
	pointSizeRandomness = 0.8
	lineLengthRandomness = 1.5

	constructor(innerContainer: THREE.Object3D) {
		super();
		this.name = "TravelingParticles"
		this.innerContainer = innerContainer;
		this.innerContainer.frustumCulled = false;

		this.uniforms = {
			uOpacity: {value: 0 },
			texture1: { value: ScenesManager.assets.particle },
			uColor: { value: new THREE.Color( 0xff0000 ) },
			uPointSize: {value: 30.0 * ScenesManager.mainScene.renderer.getPixelRatio()},
			uLineLength: {value: 0.9}, //0 to 1. 1 is maximum length. increase pointSize to increase it even further
			uLineThickness: {value: 0.12}, // 0 to 1. 1 is maximum thickness
		};

		this.material = new THREE.ShaderMaterial({
			uniforms: this.uniforms,
			vertexShader: TravelingParticlesShader.vertexShader,			
			fragmentShader: TravelingParticlesShader.fragmentShader,				
			blending: THREE.AdditiveBlending,
			transparent: true,
			depthTest: true,
			depthWrite: false
			
		});
		this.getPaths();
	}

	getGeometryPerLine(numberOfPoints: number, name: string) {
		const geometry = new THREE.BufferGeometry();
		const positions = new Float32Array(numberOfPoints * 3);
		const opacity = new Float32Array(numberOfPoints);
		const aColor = new Float32Array(numberOfPoints * 3);
		const aRotation = new Float32Array(numberOfPoints);
		const aTargetRotation = new Float32Array(numberOfPoints);
		const aRandomPosition = new Float32Array(numberOfPoints * 3);

		const aPointSize = new Float32Array(numberOfPoints);
		const aLineLength = new Float32Array(numberOfPoints);

		for (let i = 0; i < numberOfPoints; i++) {
			opacity.set([1], i);
			positions.set([Math.random()*1, Math.random()*1, 0], i*3);			
		}
		geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
		geometry.setAttribute('opacity', new THREE.BufferAttribute(new Float32Array(opacity), 1));
		geometry.setAttribute('aColor', new THREE.BufferAttribute(new Float32Array(aColor), 3));
		geometry.setAttribute('aRotation', new THREE.BufferAttribute(new Float32Array(aRotation), 1));
		geometry.setAttribute('aTargetRotation', new THREE.BufferAttribute(new Float32Array(aTargetRotation), 1));
		geometry.setAttribute('aRandomPosition', new THREE.BufferAttribute(new Float32Array(aRandomPosition), 3));
		geometry.setAttribute('aPointSize', new THREE.BufferAttribute(new Float32Array(aPointSize), 1));
		geometry.setAttribute('aLineLength', new THREE.BufferAttribute(new Float32Array(aLineLength), 1));

		
		const mesh = new THREE.Points(
			geometry,
			this.material.clone()
		)

        mesh.renderOrder = 2;
		mesh.rotation.x = -Math.PI / 2;
		mesh.layers.enable( ScenesManager.BLOOM_SCENE )

		const isInterior = ScenesManager.INTERNAL_FEATURES_DATA[name] !== undefined;
		mesh.userData.isInterior = isInterior;
		mesh.material.depthTest = true;
		mesh.material.depthWrite = false;
		
		const container = !isInterior ? this : this.innerContainer
		container.renderOrder = 2;
		container.add(mesh);	

		if (!ScenesManager.featureTravelingParticles[name]) {
			ScenesManager.featureTravelingParticles[name] = { targets: []};
		}
		ScenesManager.featureTravelingParticles[name].targets.push(mesh as unknown as THREE.Mesh)
		return {
			geometry,
			max: numberOfPoints
		}
	}

	getPaths() {

		this.lines = [];
		// this.lineMaterial = new THREE.LineBasicMaterial( { color: 0xff0000, transparent: true, opacity: ScenesManager.debugMode ? 1 : 0 } );
		this.lineMaterial = new LineMaterial( {
			color: 0xff0000,
			linewidth: 0,
			transparent: true,
			opacity: ScenesManager.debugMode ? 1 : 0,
			dashed: false,
			alphaToCoverage: true,

		} );

		let lastLineActivated : Mesh | null = null


		ScenesManager.assets.featurePaths.scene.children.forEach((line, j) =>{
			const nameCutIndex = line.name.indexOf("_line");
			line.userData.featureName = line.name.substring(0, nameCutIndex);

			if (line.userData.featureName === ScenesManager.lastAddedFeature?.name) 
			{
				this.activateLine(line as Mesh)
			}
		})
	}

	activateLine(line: Mesh | null) {
		if (!line) return;
		
		const nameCutIndex = line.name.indexOf("_line");
			line.userData.featureName = line.name.substring(0, nameCutIndex);
			
			const featureData =
			ScenesManager.EXTERNAL_FEATURES_DATA[line.userData.featureName] || ScenesManager.INTERNAL_FEATURES_DATA[line.userData.featureName];			
			
			//only show traveling particles for the last feature activated
			//show all if edit mode
			
			if (featureData && featureData.active === true) {
				if (ScenesManager.featureTraveling.indexOf(line.userData.featureName) < 0) {
					ScenesManager.featureTraveling.push(line.userData.featureName)
				}
				const posArr = (line as THREE.Mesh).geometry.attributes.position.array;
				let numberOfPoints = Math.floor(posArr.length-2);
				let points: THREE.Vector3[]= [];
				let _points: number[]= [];
				let randX = 0;
				let randY = 0;

				for (let i = 0; i < numberOfPoints; i += 3) {
					randX = (Math.random() - 0.5)*5* 0.00;
					randY = (Math.random() - 0.5)*5* 0.00;
					points.push(new THREE.Vector3(
						posArr[i] + randX,
						posArr[i+1] + randY,
						posArr[i+2] + randY,
					))
					_points.push(posArr[i] + randX,
						posArr[i+1] + randY,
						posArr[i+2] + randY)
				}
				_points.push(posArr[0] + randX, posArr[1] + randY, posArr[2] + randY);

				const curve = new THREE.CatmullRomCurve3(points, true, 'centripetal');
				const curvedPoints = curve.getPoints( numberOfPoints );
				// const geometry = new THREE.BufferGeometry().setFromPoints( curvedPoints );
				const geometry = new LineGeometry();
				geometry.setPositions( _points );

				const curveObject = new Line2(geometry, this.lineMaterial );				
				curveObject.rotation.x = -Math.PI / 2;
				curveObject.layers.enable( ScenesManager.BLOOM_SCENE )
				// this.add(curveObject)
				

				const geoData = this.getGeometryPerLine(Math.floor(curvedPoints.length / 3), line.userData.featureName);

				this.lines.push({
					number: numberOfPoints,
					points: points,
					speed: numberOfPoints > 100 ? 0.0002 : 0.015,
					curve: curve,
					geometry: geoData.geometry,
					max: geoData.max,
				})
			}
	}


	show() {		
		return new Promise(async (resolve) => {		
			for (let key in ScenesManager.featureTravelingParticles) {
				if (ScenesManager.featureTravelingParticles[key]) {
					ScenesManager.featureTravelingParticles[key].targets.forEach(item => {
						gsap.to((item.material as THREE.ShaderMaterial).uniforms.uOpacity, {
							value: !item.userData.isInterior && !ScenesManager.EXTERNAL_REVEALEAD ? 1.0 : 0.0,
							duration: 2,
							delay: 1,
							ease: "quad.inOut",
						});
					})
                }
            }
			
			// this.opacityTween?.kill();
			// this.opacityTween = gsap.to(this.material.uniforms.uOpacity, {
			// 	value: 1.0,
			// 	duration: 3,
			// 	ease: "quad.inOut",
			// });
			
			this.messTween?.kill();

			this.messTween = gsap.to(this, {
				messFactor: this.messAmount,
				duration: 1.6,
				delay: 1,
				ease: "power0.inOut",
				onStart: () => {
					CameraManager.playIntro()
				},
				onComplete: () => {
					resolve(true);
					ScenesManager.startedExperience = true;
				},
			});
		});
	}

	hide() {
		this.opacityTween?.kill();
		this.opacityTween = gsap.to(this.material.uniforms.uOpacity, {
			value: 0.0,
			duration: 1,
			delay: 1,
			ease: "quad.inOut",
		});
		this.materialTween?.kill();
		this.materialTween = gsap.to(this.lineMaterial, {
			opacity: 1.0,
			linewidth: 1.0,
			duration: 0.8,
			ease: "quad.inOut",
		});

	}

	updateOnce() {
		this.lines.forEach(line => {
			if (line.geometry) {
				for (let i = 0; i < line.geometry.attributes.position.array.length; i++) {
					let rot = (Math.random() - 0.5) * Math.PI / 2.0;
					line.geometry.attributes.aTargetRotation.setX(Math.floor(i/3), rot)
				}
			}
			line.geometry.attributes.aTargetRotation.needsUpdate = true;
		});
	}

	update(time: number) {

		// let j = 0;
		const flyTime = .0002 * performance.now();
		const length = !ScenesManager.inInterior ? 25 : 10;

		//update only if pass certain threshold
		this.delta = performance.now() - this.currentTime;
		if (this.delta > this.minDelta) {
			this.currentTime = performance.now();

			this.lines.forEach(line => {
				if (line.geometry) {
					for (let i = 0; i < line.geometry.attributes.position.array.length; i++) {
						let randX = (Math.random() - 0.5) * this.messFactor;
						let randY = (Math.random() - 0.5) * this.messFactor;
						let randZ = (Math.random() - 0.5) * this.messFactor;
						line.geometry.attributes.aRandomPosition.setXYZ(i, randX, randY, randZ);

						let rot = (Math.random() - 0.5) * Math.PI / 2.0;
						line.geometry.attributes.aTargetRotation.setX(Math.floor(i/3), rot)
					}
				}
				line.geometry.attributes.aRandomPosition.needsUpdate = true;
				line.geometry.attributes.aTargetRotation.needsUpdate = true;
			});
		}

		this.lines.forEach(line => {
			if (line.geometry) {
				for (let i = 0; i < line.geometry.attributes.position.array.length; i++) {
					const point = line.curve.getPoint(flyTime + line.speed * i);
					///line.geometry.attributes.position.setXYZ(i, point.x, point.y, point.z);

					let opacity = Math.pow(i/length, this.opacityMultipler);

					let r = 1.0;
					let g = Math.random() * 0.2 * Math.min(opacity, 1.0);
					let b = 0.0;

					let prevRot = line.geometry.attributes.aRotation.getX(Math.floor(i/3));
					let targetRot = line.geometry.attributes.aTargetRotation.getX(Math.floor(i/3));
					let rot = MathUtils.lerp(prevRot, targetRot, (Math.cos(performance.now()) + 1.0) / 2.0);

					let pointSizes =  (Math.random() * this.pointSizeRandomness + 1.0 - this.pointSizeRandomness) * opacity * 0.2;
					let lineLengths = Math.random() * this.lineLengthRandomness + (1.0 - this.lineLengthRandomness)

					let randX = line.geometry.attributes.aRandomPosition.getX(i);
					let randY = line.geometry.attributes.aRandomPosition.getY(i);
					let randZ = line.geometry.attributes.aRandomPosition.getZ(i);

					line.geometry.attributes.position.setXYZ(i, point.x + randX, point.y + randY , point.z + randZ);
					line.geometry.attributes.aColor.setXYZ(i, r, g , b);
					line.geometry.attributes.opacity.setX(i/3, opacity)
					line.geometry.attributes.aRotation.setX(i/3, rot)
					line.geometry.attributes.aPointSize.setX(i/3, pointSizes)
					line.geometry.attributes.aLineLength.setX(i/3, lineLengths)
				}
			}
			line.geometry.attributes.position.needsUpdate = true;
			line.geometry.attributes.opacity.needsUpdate = true;
			line.geometry.attributes.aColor.needsUpdate = true;
			line.geometry.attributes.aRotation.needsUpdate = true;
			line.geometry.attributes.aPointSize.needsUpdate = true;
			line.geometry.attributes.aLineLength.needsUpdate = true;
		});
	}
}